import { AvailableModules, Features } from 'generated/featureDefinitions'
import type { BillingAddonReferenceFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'

import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { toDatoCountryCode } from 'modules/upselling_kit/lib/datoLocalization'

interface Props {
  moduleRequested: AvailableModules
  featureRequested?: Features
  title?: string
  description?: string
}

export type ModuleCarouselImage = { url?: string | null }

export type ModuleFeature = {
  emoji?: string | null
  title?: string | null
  description?: string | null
}

export type GetUpsellingDataReturns = {
  moduleName?: string | null
  moduleDescription?: string | null
  /** Module avatar image url (same as ResourceHeader avatar.src — moduleData?.image?.url) */
  moduleAvatar?: string | null
  upsellModalTitle?: string | null
  upsellModalDescription?: string | null
  upsellModalImage?: string | null
  upsellModalBenefits: string[]
  moduleSlug?: string | null
  moduleCarouselVideo?: string | null
  moduleCarouselImages: ModuleCarouselImage[]
  moduleValues: ModuleFeature[]
  /** Billing addon reference for this module — use with SelfUpsellProvider to build comparison plans (same as PricingPlan). */
  billingAddonReference: readonly BillingAddonReferenceFragment[]
  /** Navattic demo thumbnail url (navatticImage.url from Dato) for Product demo section. */
  navatticImageUrl?: string | null
  loading: boolean
  moduleAvailable: boolean
  featureAvailable: boolean
  featureModals?: Array<{
    id: string
    featureSlug?: string | null
    featureSlide?: Array<{
      image?: {
        url?: string | null
      } | null
      title?: string | null
      description?: string | null
    }> | null
  }> | null
}

export const useGetUpsellingData = ({
  moduleRequested,
  featureRequested,
  title,
  description,
}: Props): GetUpsellingDataReturns => {
  const {
    company,
    access: { locale: user_locale },
  } = useCurrentSession()
  const mainCountry = company.legalEntities.find((legalEntity) => legalEntity.main)?.country
  const datoFormatLocale = getDatoLocale(user_locale)

  const { data: datoModules, loading: dataLoading } = useGetDatoModules({
    chargebeeModuleName: moduleRequested,
    countryCode: toDatoCountryCode(mainCountry || ''),
    locale: datoFormatLocale,
  })

  const { featureAvailability, loading: featureLoading } = useFeatureAvailability(
    featureRequested || '',
    company.id
  )

  const firstModule = datoModules.at(0)
  const moduleName = title || firstModule?.name
  const moduleDescription = description || firstModule?.shortDescription
  const moduleAvatar = firstModule?.image?.url ?? undefined

  const upsellModalTitle = firstModule?.upsellTitle
  const upsellModalDescription = firstModule?.upsellDescription
  const upsellModalImage = firstModule?.upsellImage?.responsiveImage?.src
    ? `${firstModule?.upsellImage?.responsiveImage?.src}?w=335&h=362&fit=scale`
    : undefined
  const upsellModalPerks = firstModule?.upsellPerks || ''
  const moduleSlug = firstModule?.slug
  const moduleCarouselVideo = firstModule?.externalUrl ?? null
  const carouselFromCarousel = firstModule?.carousel ?? []
  const carouselFromImages: ModuleCarouselImage[] =
    firstModule?.images?.map((img) => ({ url: img.url })) ?? []
  const moduleCarouselImages =
    carouselFromCarousel.length > 0 ? carouselFromCarousel : carouselFromImages
  const moduleValues = firstModule?.values ?? []
  const billingAddonReference = firstModule?.billingAddonReference ?? []
  const navatticImageUrl = firstModule?.navatticImage?.url ?? null

  const upsellModalBenefits = upsellModalPerks
    .split('.')
    .slice(0, -1)
    .map((perk) => perk.trim())

  const featureModals = firstModule?.featureModals || null

  const loading = dataLoading || (featureRequested ? featureLoading : false)
  const featureAvailable = featureRequested ? featureAvailability.available : true

  return {
    moduleName,
    moduleDescription,
    moduleAvatar,
    upsellModalTitle,
    upsellModalDescription,
    upsellModalImage,
    upsellModalBenefits,
    moduleSlug,
    moduleCarouselVideo,
    moduleCarouselImages,
    moduleValues,
    billingAddonReference,
    navatticImageUrl,
    loading,
    moduleAvailable: datoModules.length > 0,
    featureAvailable,
    featureModals,
  }
}
